import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { useModal } from '../../context/ModalContext';
import api from '../../utils/api';
import { FiUserPlus, FiSearch, FiEdit, FiTrash2, FiX, FiDownload } from 'react-icons/fi';

function RestaurantGuests() {
  const { alert, confirm } = useModal();
  const [guests, setGuests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingGuest, setEditingGuest] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    birthdate: '',
    anniversary: ''
  });

  useEffect(() => {
    fetchGuests();
  }, []);

  const fetchGuests = async () => {
    try {
      const response = await api.get('/api/guests/restaurant');
      setGuests(response.data);
    } catch (error) {
      console.error('Failed to fetch guests:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingGuest) {
        await api.put(`/api/guests/restaurant/${editingGuest.id}`, formData);
      } else {
        await api.post('/api/guests/restaurant', formData);
      }
      fetchGuests();
      setShowModal(false);
      resetForm();
    } catch (error) {
      console.error('Failed to save guest:', error);
      await alert(error.response?.data?.error || 'Failed to save guest', 'Error', 'error');
    }
  };

  const handleEdit = (guest) => {
    setEditingGuest(guest);
    setFormData({
      name: guest.name || '',
      phone: guest.phone || '',
      email: guest.email || '',
      birthdate: guest.birthdate ? guest.birthdate.split('T')[0] : '',
      anniversary: guest.anniversary ? guest.anniversary.split('T')[0] : ''
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    const confirmed = await confirm('Are you sure you want to delete this guest?', 'Delete Guest', 'warning');
    if (!confirmed) return;
    
    try {
      await api.delete(`/api/guests/restaurant/${id}`);
      fetchGuests();
    } catch (error) {
      console.error('Failed to delete guest:', error);
      await alert('Failed to delete guest', 'Error', 'error');
    }
  };

  const resetForm = () => {
    setFormData({ name: '', phone: '', email: '', birthdate: '', anniversary: '' });
    setEditingGuest(null);
  };

  const filteredGuests = guests.filter(guest =>
    guest.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    guest.phone?.includes(searchTerm) ||
    guest.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleDownloadCsv = () => {
    if (!guests.length) {
      alert('No guest data available to export', 'Export CSV', 'warning');
      return;
    }

    const formatDate = (dateValue) => {
      if (!dateValue) return '';
      const date = new Date(dateValue);
      if (Number.isNaN(date.getTime())) return '';
      return date.toISOString().split('T')[0];
    };

    const escapeCsvValue = (value) => {
      if (value === null || value === undefined) return '';
      const str = String(value).replace(/"/g, '""');
      return /[",\n]/.test(str) ? `"${str}"` : str;
    };

    const headers = [
      'Name',
      'Phone',
      'Email',
      'Birthdate',
      'Anniversary',
      'Visit Count',
      'Last Visit',
      'Created At'
    ];

    const rows = guests.map((guest) => ([
      guest.name || '',
      guest.phone || '',
      guest.email || '',
      formatDate(guest.birthdate),
      formatDate(guest.anniversary),
      guest.visit_count || 0,
      formatDate(guest.last_visit),
      formatDate(guest.created_at)
    ]));

    const csv = [headers, ...rows]
      .map((row) => row.map(escapeCsvValue).join(','))
      .join('\n');

    const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const dateSuffix = new Date().toISOString().split('T')[0];

    link.href = url;
    link.download = `restaurant-guests-${dateSuffix}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <Layout title="Restaurant Guests">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Restaurant Guests">
      <div className="space-y-6">
        {/* Header Section */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 mb-1">Walking Guests</h1>
            <p className="text-sm text-gray-600">Manage restaurant guests and their information</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleDownloadCsv}
              className="bg-white text-gray-800 border border-gray-300 px-4 py-2 rounded-lg hover:bg-gray-50 flex items-center gap-2 font-medium shadow-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={!guests.length}
            >
              <FiDownload className="text-base" /> Download CSV
            </button>
            <button
              onClick={() => {
                resetForm();
                setShowModal(true);
              }}
              className="bg-gray-900 text-white px-4 py-2 rounded-lg hover:bg-gray-800 flex items-center gap-2 font-medium shadow-sm transition-all"
            >
              <FiUserPlus className="text-base" /> Add Guest
            </button>
          </div>
        </div>

        {/* Search and Filter Bar */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
          <div className="relative">
            <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-base" />
            <input
              type="text"
              placeholder="Search by name, phone, or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent bg-white text-sm"
            />
          </div>
        </div>

        {filteredGuests.length === 0 ? (
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-16 text-center">
            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <FiUserPlus className="text-4xl text-gray-400" />
            </div>
            <p className="text-gray-700 text-xl font-semibold mb-2">No guests found</p>
            <p className="text-gray-500 text-sm mb-6">
              {searchTerm ? 'Try a different search term' : 'Add your first walking guest to get started'}
            </p>
            {!searchTerm && (
              <button
                onClick={() => {
                  resetForm();
                  setShowModal(true);
                }}
                className="bg-gray-900 text-white px-6 py-3 rounded-lg hover:bg-gray-800 flex items-center gap-2 mx-auto font-medium shadow-sm transition-all"
              >
                <FiUserPlus /> Add First Guest
              </button>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
            {/* Desktop Table View */}
            <div className="hidden lg:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Phone</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Email</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Birthdate</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Anniversary</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Visits</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Last Visit</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {filteredGuests.map((guest) => (
                    <tr key={guest.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="font-semibold text-gray-900">{guest.name}</div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-gray-700">{guest.phone || <span className="text-gray-400">-</span>}</span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-gray-700">{guest.email || <span className="text-gray-400">-</span>}</span>
                      </td>
                      <td className="px-6 py-4">
                        {guest.birthdate ? (
                          <span className="text-sm text-gray-700">{new Date(guest.birthdate).toLocaleDateString('en-IN')}</span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        {guest.anniversary ? (
                          <span className="text-sm text-gray-700">{new Date(guest.anniversary).toLocaleDateString('en-IN')}</span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-semibold bg-blue-100 text-blue-800">
                          {guest.visit_count || 1}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        {guest.last_visit ? (
                          <span className="text-sm text-gray-700">{new Date(guest.last_visit).toLocaleDateString('en-IN')}</span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex gap-3">
                          <button
                            onClick={() => handleEdit(guest)}
                            className="text-gray-600 hover:text-gray-900 transition-colors p-1.5 hover:bg-gray-100 rounded"
                            title="Edit guest"
                          >
                            <FiEdit className="text-lg" />
                          </button>
                          <button
                            onClick={() => handleDelete(guest.id)}
                            className="text-red-600 hover:text-red-900 transition-colors p-1.5 hover:bg-red-50 rounded"
                            title="Delete guest"
                          >
                            <FiTrash2 className="text-lg" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile/Tablet Card View */}
            <div className="lg:hidden divide-y divide-gray-200">
              {filteredGuests.map((guest) => (
                <div key={guest.id} className="p-4 hover:bg-gray-50 transition-colors">
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-900 mb-1 truncate">{guest.name}</h3>
                      <p className="text-sm text-gray-600 mb-1">{guest.phone || '-'}</p>
                      {guest.email && <p className="text-xs text-gray-500 truncate">{guest.email}</p>}
                    </div>
                    <div className="flex gap-2 ml-2 flex-shrink-0">
                      <button
                        onClick={() => handleEdit(guest)}
                        className="text-gray-600 hover:text-gray-900 transition-colors p-1.5 hover:bg-gray-100 rounded"
                        title="Edit guest"
                      >
                        <FiEdit className="text-lg" />
                      </button>
                      <button
                        onClick={() => handleDelete(guest.id)}
                        className="text-red-600 hover:text-red-900 transition-colors p-1.5 hover:bg-red-50 rounded"
                        title="Delete guest"
                      >
                        <FiTrash2 className="text-lg" />
                      </button>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 items-center text-xs">
                    {guest.birthdate && (
                      <span className="text-gray-600">
                        Birthdate: {new Date(guest.birthdate).toLocaleDateString('en-IN')}
                      </span>
                    )}
                    {guest.anniversary && (
                      <span className="text-gray-600">
                        Anniversary: {new Date(guest.anniversary).toLocaleDateString('en-IN')}
                      </span>
                    )}
                    <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-semibold bg-blue-100 text-blue-800">
                      {guest.visit_count || 1} visits
                    </span>
                    {guest.last_visit && (
                      <span className="text-gray-600">
                        Last: {new Date(guest.last_visit).toLocaleDateString('en-IN')}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-900">
                {editingGuest ? 'Edit Guest' : 'Add New Guest'}
              </h2>
              <button
                onClick={() => {
                  setShowModal(false);
                  resetForm();
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors p-1.5 hover:bg-gray-100 rounded"
              >
                <FiX className="text-xl" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm"
                  placeholder="Enter guest name"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number *</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm"
                  required
                  placeholder="e.g., +91 9876543210"
                />
                <p className="text-xs text-gray-500 mt-1">Required for identification</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm"
                  placeholder="guest@example.com (optional)"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Birthdate</label>
                  <input
                    type="date"
                    value={formData.birthdate}
                    onChange={(e) => setFormData({ ...formData, birthdate: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm"
                    max={new Date().toISOString().split('T')[0]}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Anniversary</label>
                  <input
                    type="date"
                    value={formData.anniversary}
                    onChange={(e) => setFormData({ ...formData, anniversary: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm"
                    max={new Date().toISOString().split('T')[0]}
                  />
                </div>
              </div>
              <div className="flex gap-3 pt-4 border-t border-gray-200">
                <button
                  type="submit"
                  className="flex-1 bg-gray-900 text-white py-3 rounded-lg hover:bg-gray-800 font-semibold shadow-sm transition-all"
                >
                  {editingGuest ? 'Update' : 'Add'} Guest
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                  className="flex-1 bg-gray-200 text-gray-800 py-3 rounded-lg hover:bg-gray-300 font-semibold transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}

export default RestaurantGuests;


